import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { useMutation } from 'react-query';
import client from '../../../../../API'; // Adjust the path to your API client
import { userExtras } from '../../../../../Utils/constants';
import AlertDialog from '../../../../../Components/AlertDialog';
import useAlertDialog from '../../../../../Components/useAlertDialog';
import { toast } from 'sonner';

const EditUserDialog = ({ open, onClose, userInfo }) => {
  const [userDetails, setUserDetails] = useState(userInfo || {});
  const [extras, setExtras] = useState([]);
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  useEffect(() => {
    setUserDetails(userInfo || {});
    setExtras(userInfo?.extras || []);
  }, [userInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserDetails(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setUserDetails(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const mutation = useMutation(
    (updatedUser) => client.users.editUserWithCredits(updatedUser),
    {
      onSuccess: () => {
        toast.success('User updated successfully', {
          description: 'Changes have been saved'
        });
        onClose(true);
      },
      onError: (error) => {
        const errorMsg = error?.response?.data?.error || error?.message || 'Unknown';
        openDialog(
          'Error',
          <>Error occurred:<br />{errorMsg}</>,
          closeDialog,
          closeDialog,
          'Ok'
        );
        console.error('Error editing user', error);
      },
    }
  );

  const handleSubmit = () => {
    const updatedUser = { ...userDetails };
    updatedUser.extras = extras.length > 0 ? extras : null;
    mutation.mutate(updatedUser);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)}>
      <AlertDialog alertState={dialogState} />
      <DialogTitle>Edit {userDetails.id ? 'User' : 'Creator User'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Username"
          name="username"
          fullWidth
          margin="dense"
          value={userDetails.username || ''}
          onChange={handleChange}
          required
        />

        {userDetails.id ? (
          <>
            <TextField
              label="First Name"
              name="first_name"
              fullWidth
              margin="dense"
              value={userDetails.first_name || ''}
              onChange={handleChange}
              required
            />
            <TextField
              label="Last Name"
              name="last_name"
              fullWidth
              margin="dense"
              value={userDetails.last_name || ''}
              onChange={handleChange}
              required
            />
            <TextField
              label="Email"
              name="email"
              fullWidth
              margin="dense"
              value={userDetails.email || ''}
              onChange={handleChange}
              required
            />
            <TextField
              label="Phone Number"
              name="phone_number"
              type="tel"
              fullWidth
              margin="dense"
              value={userDetails.phone_number || ''}
              onChange={handleChange}
            />
            <TextField
              label="Company ID"
              name="company_id"
              fullWidth
              margin="dense"
              value={userDetails.company_id || ''}
              onChange={handleChange}
            />
            <TextField
              label="Company Name"
              name="company_name"
              fullWidth
              margin="dense"
              value={userDetails.company_name || ''}
              onChange={handleChange}
              required
            />
            <TextField
              label="Address"
              name="address"
              fullWidth
              margin="dense"
              value={userDetails.address || ''}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={userDetails.status ? 'Active' : 'Inactive'}
                onChange={handleChange}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            {/* Supervisor toggle */}
            <FormControlLabel
              control={
                <Checkbox
                  name="supervisor"
                  checked={!!userDetails.supervisor}
                  onChange={handleCheckboxChange}
                />
              }
              label="Supervisor"
            />

            <TextField
              label="Type"
              name="type"
              fullWidth
              margin="dense"
              value={userDetails.type || ''}
              onChange={handleChange}
              required
            />
            <TextField
              label="PHPHref"
              name="phphref"
              fullWidth
              margin="dense"
              value={userDetails.phphref || ''}
              onChange={handleChange}
            />
            <TextField
              label="Credits"
              name="credits"
              type="number"
              fullWidth
              margin="dense"
              value={userDetails.credits || ''}
              onChange={handleChange}
            />
            <Autocomplete
              multiple
              id="tags-standard"
              options={userExtras}
              value={extras}
              onChange={(event, newValue) => {
                setExtras(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  label="Extras"
                  placeholder="Extras"
                />
              )}
            />
          </>
        ) : (
          <>
            <TextField
              label="Balance"
              name="balance"
              fullWidth
              margin="dense"
              value={userDetails.balance || ''}
              onChange={handleChange}
              required
            />
            <TextField
              label="Payout Preferred"
              name="payout_preferred"
              fullWidth
              margin="dense"
              value={userDetails.payout_preferred || ''}
              onChange={handleChange}
            />
            <TextField
              label="Stripe ID"
              name="stripe_id"
              fullWidth
              margin="dense"
              value={userDetails.stripe_id || ''}
              onChange={handleChange}
            />
            <TextField
              label="PayPal"
              name="paypal"
              fullWidth
              margin="dense"
              value={userDetails.paypal || ''}
              onChange={handleChange}
            />
            <TextField
              label="Email"
              name="email"
              fullWidth
              margin="dense"
              value={userDetails.email || ''}
              onChange={handleChange}
              required
            />
            <TextField
              label="Credits"
              name="credits"
              type="number"
              fullWidth
              margin="dense"
              value={userDetails.credits || ''}
              onChange={handleChange}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button onClick={handleSubmit}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
